import type { GuidedProcedure } from '@padcanvas/shared';
import type { ChemistryBenchApi } from '@shared-ui/chemistry-lab/ChemistryBench';
import type { ChemistryLabActionResponse, ChemistryLabSession } from '@shared-ui/chemistry-lab/types';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';
import type { ApiResponse } from '../types';
import { getErrorMessage } from '../types';
import type { CurationChanges } from './simulationsApi';

/**
 * The bench itself, driven with the admin's own token.
 *
 * The endpoints are the student app's — they take any signed-in user — so this
 * is the same bench the students get, metered the same way against this
 * account's own quota. It exists here so an operator can see what the
 * quantitative bench actually produces without turning it on for anyone.
 */
export const adminChemistryLabApi: ChemistryBenchApi = {
  startSession: (mode) =>
    fetchWithAdmin<ChemistryLabSession>('/chemistry-lab/session', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  action: (contents, temperatureBand, action, sessionId, mixPhase) =>
    fetchWithAdmin<ChemistryLabActionResponse>('/chemistry-lab/action', {
      method: 'POST',
      body: JSON.stringify({ contents, temperatureBand, action, sessionId, mixPhase }),
    }),
};

export type ReactionStatus = 'pending_review' | 'approved' | 'rejected';

export interface LabReaction {
  mixKey: string;
  substanceIds: string[];
  temperatureBand: string;
  outcome: Record<string, unknown> & { narrationText?: string; equation?: string; confidence?: string };
  status: ReactionStatus;
  source: 'llm' | 'seed';
  hitCount: number;
  lastSeenAt: string;
  createdAt: string;
  modelRunId?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

/** A stored experiment: the authored procedure plus the chapters it resolved to
 *  in THIS environment. See docs/FeatureDesigns/chemistry-lab-guided-target.md. */
export interface GuidedExperimentAdmin extends GuidedProcedure {
  moduleIds: string[];
  chapterNames: string[];
  /** Curation, declared here rather than inherited: the repo catalogue no
   *  longer carries `status`, because publishing is an editorial act performed
   *  in admin, not a consequence of a deploy. Mirrors the server's
   *  GuidedExperimentView (functions/src/chemistryLab/guidedExperimentTypes.ts). */
  status: 'live' | 'draft';
  /** Flat board-grade keys ('cbse:10', 'jee') — the same vocabulary questions
   *  and simulations use. One entity, two runtimes. */
  boardGrades?: string[];
  /** Absent means boardGrades is still the seed's proposal, not a decision. */
  boardGradesCuratedAt?: string;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const token = authApi.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    authApi.logout();
    throw new Error('Session expired. Please login again.');
  }
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(getErrorMessage(data.error) || `API error: ${res.status} ${res.statusText}`);
  }
  return data.data as T;
}

export const chemistryLabApi = {
  listReactions: (status: ReactionStatus) =>
    fetchWithAdmin<{ reactions: LabReaction[]; counts: Record<ReactionStatus, number> }>(
      `/console/chemistry-lab/reactions?status=${status}`,
    ),

  /** Approving means a named person stands behind this chemistry being shown
   *  to students; from then on it is served with no model call. */
  review: (mixKey: string, status: ReactionStatus, outcome?: Record<string, unknown>) =>
    post<{ reaction: LabReaction }>(
      `/console/chemistry-lab/reactions/${encodeURIComponent(mixKey)}/review`,
      { status, ...(outcome ? { outcome } : {}) },
    ),

  // ── Guided experiments ────────────────────────────────────────────
  listExperiments: () =>
    fetchWithAdmin<{ experiments: GuidedExperimentAdmin[] }>('/console/chemistry-lab/experiments')
      .then(r => r.experiments),

  /** Status, chapters and audience — all admin changes on an experiment. */
  curateExperiment: (id: string, changes: CurationChanges) =>
    fetchWithAdmin<{ experiment: GuidedExperimentAdmin }>(
      `/console/chemistry-lab/experiments/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(changes) },
    ).then(r => r.experiment),
};
